import React, { useState, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Box,
  FormControlLabel,
  Radio,
} from "@material-ui/core";
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CropFreeIcon from '@material-ui/icons/CropFree';
import SignalCellularConnectedNoInternet2BarIcon from '@material-ui/icons/SignalCellularConnectedNoInternet2Bar';

import {
  Facebook,
  Instagram,
} from "@material-ui/icons";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import HeaderManager from "../../components/HeaderTableManager/HeaderTableManager";
import TableManager from "../../components/TableManager/TableManager";
import TrashIcon from "../../assets/icon/TrashIcon";
import PencilEditIcon from "../../assets/icon/PencilEditIcon";
import WhatsAppIcon from "../../assets/icon/WhatsAppIcon";
import WhatsAppIconCloud from "../../assets/icon/WhatsAppIconCloud";
import MessengerIcon from "../../assets/icon/MessengerIcon";
import { AuthContext } from "../../context/Auth/AuthContext";
import AddIcon from "../../assets/icon/AddIcon";
import SelectChannelModal from "../../components/SelectChannelModal";
import WebChatModal from "../../components/WebChatModal";
import {
  buildBusinessConnectPayload,
  launchWhatsAppCloudSignup,
} from "../../utils/whatsappCloudSignup";
import {
  isInstagramDmChannel,
  isMessengerPageChannel,
  isWhatsappApiChannel,
} from "../../utils/connectionChannel";
import { launchInstagramPageOAuth } from "../../utils/instagramPageOAuth";
import { launchMessengerPageOAuth } from "../../utils/messengerPageOAuth";
import {
  getQualityIndicatorType,
  META_BUSINESS_SUITE_PHONE_URL,
  normalizeQualityRating,
} from "../../utils/cloudPhoneQuality";
import smsIcon from "../../assets/img/chat.png";
import StatusIndicator from "../../components/StatusIndicator/StatusIndicator";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  tableContainer: {
    width: "100%",
    maxWidth: "80%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "90%",
    },
    [theme.breakpoints.down("md")]: {
      maxWidth: "90%",
    },
    [theme.breakpoints.up("lg")]: {
      maxWidth: "90%",
    },
  },
  buttonQR: {
    borderRadius: 10,
    width: 100,
  },
  buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
  channelIconWrapper: {
    position: "relative",
    width: 40,
    height: 40,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginLeft: 10,
  },
  channelIconSvg: {
    width: 22,
    height: 22,
  },
  channelIconCloud: {
    width: 32,
    height: 32,
  },
  channelIconImage: {
    width: 22,
    height: 22,
    objectFit: "contain",
  },
}));

// los atributos title y content no funcionan, lo que muestra realmente es lo que se le manda en children
// TODO: Mejorar funcionamiento del Functional Component CustomToolTip
const CustomToolTip = ({ title, content, children }) => {
  return (
    <Tooltip
      arrow
      title={
        <Box>
          <Typography gutterBottom color="inherit">
            {title}
          </Typography>
          {content && <Typography>{content}</Typography>}
        </Box>
      }
    >
      {/* Elemento span importante para visualizar el tooltip */}
      <span>
        {children}
      </span>
    </Tooltip>
  );
};

const ChannelIconWrapper = ({ classes, backgroundColor, children }) => (
  <div
    className={classes.channelIconWrapper}
    style={{ backgroundColor }}
  >
    {children}
  </div>
);

const ChannelIcon = ({ channel, classes }) => {
  if (channel === "whatsapp") {
    return (
      <ChannelIconWrapper classes={classes} backgroundColor="rgba(37, 211, 102, 0.12)">
        <WhatsAppIcon fill="#25D366" width="28" height="28" />
      </ChannelIconWrapper>
    );
  }

  if (channel === "whatsapp-api") {
    return (
      <ChannelIconWrapper classes={classes} backgroundColor="rgba(37, 211, 102, 0.12)">
        <WhatsAppIconCloud className={classes.channelIconCloud} fill="#25D366" width="28" height="28" />
      </ChannelIconWrapper>
    );
  }

  if (channel === "messenger-page") {
    return (
      <ChannelIconWrapper classes={classes} backgroundColor="rgba(33, 150, 243, 0.18)">
        <MessengerIcon width="22" height="22" />
      </ChannelIconWrapper>
    );
  }

  if (channel === "webchat") {
    return (
      <ChannelIconWrapper classes={classes} backgroundColor="rgba(0, 123, 255, 0.18)">
        <img src={smsIcon} alt="WebChat" className={classes.channelIconImage} />
      </ChannelIconWrapper>
    );
  }

  if (channel === "instagram-dm" || channel === "instagram") {
    return (
      <ChannelIconWrapper classes={classes} backgroundColor="rgba(225, 48, 108, 0.12)">
        <Instagram style={{ color: "#E1306C", fontSize: 22 }} />
      </ChannelIconWrapper>
    );
  }

  return <div />;
};

const Connections = () => {
  const classes = useStyles();
  const { whatsApps, loading, dispatch } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalInfo, setConfirmModalInfo] = useState({
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    confirmButtonText: "",
  });
  const [selectChannelModalOpen, setSelectChannelModalOpen] = useState(false);
  const [selectedChannelType, setSelectedChannelType] = useState(null);
  const [webChatModalOpen, setWebChatModalOpen] = useState(false);
  const [selectedWebChatId, setSelectedWebChatId] = useState(null);
  const [isLoadingQueue, setIsLoadingQueue] = useState([]); // state para colas de peticiones (isLoading)
  
  /**
   * 
   * @param {number} whatsAppId WhatsApp Id
   * @param {'Connection' | 'Deletion' | 'Default'} type 
   * @returns 
   */
  const addRecordIsLoadingQueue = (whatsAppId, type) => setIsLoadingQueue((prev) => ([...prev, { id: whatsAppId, isLoading: true, type }]))
  const deleteRecordIsLoadingQueue = (whatsAppId) => setIsLoadingQueue((prev) => prev.filter(item => item.id !== whatsAppId));

  const handleRequestNewQrCode = async (whatsAppId) => {
    addRecordIsLoadingQueue(whatsAppId, 'Connection')
    try {
      await api.put(`/whatsapp/${whatsAppId}/session`);
    } catch (err) {
      console.log(err);
      toastError(err);
    } finally {
      deleteRecordIsLoadingQueue(whatsAppId)
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, []);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, []);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
        confirmButtonText: i18n.t("connections.buttons.ConfirmDeleting"),
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    const whatsAppId = confirmModalInfo.whatsAppId;
    const isDeleting = confirmModalInfo.action === "delete";
    addRecordIsLoadingQueue(whatsAppId, 'Deletion')
    if (!isDeleting) {
      try {
        await api.delete(`/whatsapp/${whatsAppId}/session`);
      } catch (err) {
        toastError(err);
      } finally {
        deleteRecordIsLoadingQueue(whatsAppId)
      }
    }

    if (isDeleting) {
      try {
        await api.delete(`/whatsapp/${whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      } finally {
        deleteRecordIsLoadingQueue(whatsAppId)
      }
    }
    setConfirmModalInfo({
      action: "",
      title: "",
      message: "",
      whatsAppId: "",
    });
    setConfirmModalOpen(false);
  };

  const handleSaveWhatsApp = async (whatsAppId, isDefault) => {
    const newIsDefault = !isDefault;
    const whatsappData = { isDefault: newIsDefault };
    addRecordIsLoadingQueue(whatsAppId, 'Default');
    
    try {
      if (whatsAppId) {
        console.log("Iniciando actualización de isDefault:", whatsAppId, "nuevo valor:", newIsDefault);
        
        // Obtener el canal de la conexión que estamos marcando como predeterminada
        const currentWhatsApp = whatsApps.find(w => w.id === whatsAppId);
        
        if (!currentWhatsApp) {
          toast.error("No se pudo encontrar la conexión seleccionada.");
          return;
        }

        // Variable para almacenar el estado actualizado (lo usaremos varias veces)
        let updatedWhatsApps;

        // PRIMERO: Actualización optimista de la interfaz
        if (currentWhatsApp && currentWhatsApp.channel && newIsDefault) {
          console.log("Actualizando conexión a predeterminada y quitando predeterminada a otras del mismo canal:", currentWhatsApp.channel);
          
          // Crear una copia del estado actual para modificarla
          updatedWhatsApps = [...whatsApps].map(wa => {
            // Si es la conexión que estamos cambiando, actualizarla a predeterminada
            if (wa.id === whatsAppId) {
              return { ...wa, isDefault: true };
            }
            
            // Si es otra conexión del mismo canal y está marcada como predeterminada, quitarla
            if (wa.channel === currentWhatsApp.channel && wa.isDefault && wa.id !== whatsAppId) {
              console.log("Quitando predeterminada de:", wa.name);
              return { ...wa, isDefault: false };
            }
            
            // Dejar las demás conexiones sin cambios
            return wa;
          });
        } else {
          // Actualizar solo la conexión actual
          console.log("Actualizando solo la conexión actual:", currentWhatsApp.name);
          updatedWhatsApps = [...whatsApps].map(wa => {
            if (wa.id === whatsAppId) {
              return { ...wa, isDefault: newIsDefault };
            }
            return wa;
          });
        }
        
        // Actualizar la interfaz inmediatamente
        console.log("Actualizando interfaz (1/2)");
        dispatch({ type: "LOAD_WHATSAPPS", payload: updatedWhatsApps });     
        // SEGUNDO: Enviar la solicitud al servidor

        console.log("Enviando solicitud al servidor");
        const response = await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
        
        if (response.status === 200) {
          console.log("Respuesta exitosa del servidor");
          
          // Mostrar notificación de éxito
          toast.success("Conexión actualizada correctamente", {
            autoClose: 1500, 
            position: "bottom-right"
          });
          
          // IMPORTANTE: Asegurarnos que la UI refleje nuestros cambios
          // Volvemos a aplicar nuestra actualización para asegurarnos que no fue sobrescrita
          console.log("Forzando actualización final (2/2)");
          setTimeout(() => {
            dispatch({ type: "LOAD_WHATSAPPS", payload: updatedWhatsApps });
          }, 500);
        } else {
          console.log("Error en respuesta del servidor:", response.status);
          
          // Si hubo un error en el servidor, revertir los cambios locales
          toast.error("Error en el servidor. Revirtiendo cambios...");
          const { data } = await api.get(`/whatsapp?session=0&companyId=${user.companyId}`);
          if (data) {
            dispatch({ type: "LOAD_WHATSAPPS", payload: data });
          }
        }
      } else {
        toast.error("No se pudo actualizar la cuenta predeterminada.");
      }
    } catch (err) {
      console.error("Error en handleSaveWhatsApp:", err);
      
      // Si hay un error, intentar recargar los datos originales
      toastError(err);
      toast.error("Error al actualizar la conexión. Recargando datos...", {
        autoClose: 3000,
        position: "bottom-right"
      });
      
      try {
        const { data } = await api.get(`/whatsapp?session=0&companyId=${user.companyId}`);
        if (data) {
          dispatch({ type: "LOAD_WHATSAPPS", payload: data });
        }
      } catch (refreshErr) {
        console.error("Error al recuperar datos originales:", refreshErr);
      }
    } finally {
      deleteRecordIsLoadingQueue(whatsAppId);
    }
  };

  const handleWebChatConnection = async (whatsAppId, currentStatus) => {
    addRecordIsLoadingQueue(whatsAppId, 'Connection');
    try {
      const newStatus = currentStatus === "DISCONNECTED" ? "CONNECTED" : "DISCONNECTED";
      await api.put(`/whatsapp/${whatsAppId}`, { status: newStatus });
      toast.success(
        newStatus === "CONNECTED"
          ? "WebChat conectado exitosamente"
          : "WebChat desconectado exitosamente"
      );
    } catch (err) {
      console.error(err);
      toastError(err);
    } finally {
      deleteRecordIsLoadingQueue(whatsAppId)
    }
  };

  const refreshWhatsApps = async () => {
    try {
      const companyId = localStorage.getItem("companyId");
      if (!companyId) {
        return;
      }
      const { data } = await api.get(`/whatsapp?session=0&companyId=${companyId}`);
      const safeData =
        data === null || data === undefined
          ? []
          : Array.isArray(data)
            ? data
            : [data];
      dispatch({ type: "LOAD_WHATSAPPS", payload: safeData });
    } catch (err) {
      toastError(err);
    }
  };

  const handleWhatsAppApiDisconnect = async (whatsAppId) => {
    try {
      await api.put(`/whatsapp/${whatsAppId}/disconnect-api`);
      toast.success("WhatsApp API desconectado exitosamente");
    } catch (err) {
      console.error(err);
      toastError(err);
    }
  };

  const handleMessengerConnect = async (whatsApp) => {
    addRecordIsLoadingQueue(whatsApp.id, "Connection");
    try {
      await launchMessengerPageOAuth({ whatsappId: whatsApp.id });
      await refreshWhatsApps();
      toast.success("Messenger reconectado con Meta");
    } catch (err) {
      if (err?.message !== "signup_canceled") {
        console.error(err);
        toastError(err);
      }
    } finally {
      deleteRecordIsLoadingQueue(whatsApp.id);
    }
  };

  const handleInstagramConnect = async (whatsApp) => {
    addRecordIsLoadingQueue(whatsApp.id, "Connection");
    try {
      await launchInstagramPageOAuth({ whatsappId: whatsApp.id });
      await refreshWhatsApps();
      toast.success("Instagram reconectado con Meta");
    } catch (err) {
      if (err?.message !== "signup_canceled") {
        console.error(err);
        toastError(err);
      }
    } finally {
      deleteRecordIsLoadingQueue(whatsApp.id);
    }
  };

  const handleWhatsAppApiConnect = async (whatsApp) => {
    addRecordIsLoadingQueue(whatsApp.id, "Connection");
    try {
      const signup = await launchWhatsAppCloudSignup({ whatsappId: whatsApp.id });

      if (signup.code) {
        await api.post(
          "/whatsapp/business",
          buildBusinessConnectPayload({
            ...signup,
            whatsappId: whatsApp.id,
          }),
          { timeout: 30000 },
        );
      }

      await refreshWhatsApps();
      toast.success("WhatsApp Cloud reconectado con Meta");
    } catch (err) {
      if (err?.message !== "signup_canceled") {
        console.error(err);
        toastError(err);
      }
    } finally {
      deleteRecordIsLoadingQueue(whatsApp.id);
    }
  };

  const NameAndUpdateInfo = ({ name, updatedAt, channel }) => {
    const formatDate = (date) => {
      try {
        if (!date && name === "Sin actualización") return "Esperando registro...";
        if (!date) return "En proceso de registro";
        return `últ. actualización ${format(parseISO(date), "dd/MM/yy HH:mm")}`;
      } catch (error) {
        console.error("Error al formatear fecha:", error);
        return "Error en registro";
      }
    };

    const isLoading = !updatedAt && name !== "Sin actualización";

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span style={{ fontWeight: "bold", fontSize: 16 }}>{name}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ 
            fontSize: 14,
            color: !updatedAt ? "#ffa726" : "#666"
          }}>
            {formatDate(updatedAt)}
          </span>
          {isLoading && <CircularProgress size={12} style={{ color: "#ffa726" }} />}
        </div>
      </div>
    );
  };
  
  const handleEditWebChat = (webChat) => {
    setSelectedWebChatId(webChat.id);
    setWebChatModalOpen(true);
  };

  const handleCloseWebChatModal = () => {
    setSelectedWebChatId(null);
    setWebChatModalOpen(false);
  };

  const handleChannelSelect = (channelType) => {
    setSelectChannelModalOpen(false);
    
    // Guardar el tipo de canal seleccionado
    setSelectedChannelType(channelType);
    
    // Abrir el modal correspondiente según el tipo de canal
    if (channelType === "webchat") {
      // Para WebChat, abrir el modal de WebChat con id null (crear nuevo)
      setSelectedWebChatId(null);
      setWebChatModalOpen(true);
    } else if (channelType === "whatsapp") {
      // Para WhatsApp tradicional (QR), usar el modal existente
      setWhatsAppModalOpen(true);
    } else if (channelType === "whatsapp_api" || channelType === "messenger") {
      void refreshWhatsApps();
    }
  };

  const handleOpenSelectChannelModal = () => {
    setSelectChannelModalOpen(true);
  };

  const primaryButtons = user.profile === "admin" ? [
    { 
      label: "Agregar conexión", 
      onClick: handleOpenSelectChannelModal, 
      icon: <AddIcon fill='#000' /> 
    },
  ] : [];

  const columns = [
    {
      field: "channel",
      renderCell: ({ row }) => <ChannelIcon channel={row.channel} classes={classes} />,
      width: "5%",
    },
    {
      field: "name",
      renderCell: ({ row }) => <NameAndUpdateInfo name={row.name} updatedAt={row.updatedAt} channel={row.channel} />,
    },
    {
      field: "status",
      renderCell: ({ row }) => renderStatusToolTips(row),
      width: "20%",
    },
    {
      field: "isDefault",
      renderCell: ({ row }) => {
        const loadInfo = isLoadingQueue.find((item) => item.id === row.id);
        const isLoad = loadInfo?.isLoading && loadInfo.type === 'Default'
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FormControlLabel
              disabled={isLoad || user.profile !== "admin"}
              checked={!!row.isDefault}
              control={
                <Radio
                  onChange={() => handleSaveWhatsApp(row.id, row.isDefault)}
                />
              }
              label={i18n.t("whatsappModal.form.default")}
            />
          </div>
        )
      },
    },
    {
      field: "session",
      renderCell: user.profile === "admin" ? ({ row }) => renderActionButtons(row) : null,
    },
  ];

  const actionButtons = user.profile === "admin" ? [
    ({ row }) => (
      <IconButton
        size="small"
        onClick={() => {
          // Verificar el tipo de canal y llamar a la función correspondiente
          if (row.channel === "webchat") {
            handleEditWebChat(row);
          } else {
            handleEditWhatsApp(row);
          }
        }}
        style={{ marginTop: 17 }}
      >
        <PencilEditIcon fill="#7F7F7F" />
      </IconButton>
    ),
    ({ row }) => (
      <IconButton
        size="small"
        onClick={() => handleOpenConfirmationModal("delete", row.id)}
        style={{ marginTop: 17 }}
      >
        <TrashIcon fill="#7F7F7F" />
      </IconButton>
      ),
    ]
  : [];

  const renderActionButtons = (whatsApp) => {
    // Para conexiones de WebChat
    if (whatsApp.channel === "webchat") {
      const loadInfo = isLoadingQueue.find((item) => item.id === whatsApp.id);
      const isLoad = loadInfo?.isLoading && loadInfo.type === 'Connection'
      return (
        <ButtonWithSpinner
          loading={isLoad}
          disabled={isLoad}
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => handleWebChatConnection(whatsApp.id, whatsApp.status)}
          className={classes.buttonQR}
          style={{}}
        >
          {whatsApp.status === "DISCONNECTED" ? "Conectar" : "Desconectar"}
        </ButtonWithSpinner>
      );
    }

    if (isMessengerPageChannel(whatsApp.channel)) {
      const loadInfo = isLoadingQueue.find((item) => item.id === whatsApp.id);
      const isLoad = loadInfo?.isLoading && loadInfo.type === "Connection";
      if (whatsApp.status === "CONNECTED") {
        return (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => handleWhatsAppApiDisconnect(whatsApp.id)}
            className={classes.buttonQR}
          >
            {i18n.t("connections.buttons.disconnect")}
          </Button>
        );
      }
      return (
        <ButtonWithSpinner
          loading={isLoad}
          disabled={isLoad}
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => handleMessengerConnect(whatsApp)}
          className={classes.buttonQR}
        >
          Recuperar Page
        </ButtonWithSpinner>
      );
    }

    if (isInstagramDmChannel(whatsApp.channel)) {
      const loadInfo = isLoadingQueue.find((item) => item.id === whatsApp.id);
      const isLoad = loadInfo?.isLoading && loadInfo.type === "Connection";
      if (whatsApp.status === "CONNECTED") {
        return (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => handleWhatsAppApiDisconnect(whatsApp.id)}
            className={classes.buttonQR}
          >
            {i18n.t("connections.buttons.disconnect")}
          </Button>
        );
      }
      return (
        <ButtonWithSpinner
          loading={isLoad}
          disabled={isLoad}
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => handleInstagramConnect(whatsApp)}
          className={classes.buttonQR}
        >
          Recuperar Instagram
        </ButtonWithSpinner>
      );
    }

    // Para conexiones de WhatsApp API
    if (isWhatsappApiChannel(whatsApp.channel)) {
      const loadInfo = isLoadingQueue.find((item) => item.id === whatsApp.id);
      const isLoad = loadInfo?.isLoading && loadInfo.type === "Connection";
      if (whatsApp.status === "CONNECTED") {
        return (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => handleWhatsAppApiDisconnect(whatsApp.id)}
            className={classes.buttonQR}
          >
            {i18n.t("connections.buttons.disconnect")}
          </Button>
        );
      }
      return (
        <ButtonWithSpinner
          loading={isLoad}
          disabled={isLoad}
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => handleWhatsAppApiConnect(whatsApp)}
          className={classes.buttonQR}
        >
          {i18n.t("connections.buttons.recoverCloud")}
        </ButtonWithSpinner>
      );
    }

    // Para conexiones de WhatsApp tradicional (QR)
    console.log('whatsApp.status', whatsApp.status)
    if (whatsApp.status === "qrcode" ) {
      return (
        <Button
          size="small"
          variant="contained"
          color="primary"
          className={classes.buttonQR}
          onClick={() => handleOpenQrModal(whatsApp)}
        >
          {i18n.t("connections.buttons.qrcode")}
        </Button>
      );
    }
    if (whatsApp.status === "DISCONNECTED") {
      const loadInfo = isLoadingQueue.find((item) => item.id === whatsApp.id)
      const isLoad = loadInfo?.isLoading && loadInfo.type === 'Connection'
      return (
        <ButtonWithSpinner
          loading={isLoad}
          disabled={isLoad}
          style={{ /** cancelar estilos del componente */ }}
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => handleRequestNewQrCode(whatsApp.id)}
          className={classes.buttonQR}
        >
          {i18n.t("connections.buttons.newQr")}
        </ButtonWithSpinner>
      );
    }
    if (["CONNECTED", "PAIRING", "TIMEOUT"].includes(whatsApp.status)) {
      const loadInfo = isLoadingQueue.find((item) => item.id === whatsApp.id);
      const isLoad = loadInfo?.isLoading && loadInfo.type === 'Connection'
      return (
        <ButtonWithSpinner
          loading={isLoad}
          disabled={isLoad}
          style={{ /** cancelar estilos del componente */ }}
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => {
            handleOpenConfirmationModal("disconnect", whatsApp.id);
          }}
          className={classes.buttonQR}
        >
          {i18n.t("connections.buttons.disconnect")}
        </ButtonWithSpinner>
      );
    }
    if (whatsApp.status === "OPENING") {
      return (
        <Button size="small" variant="outlined" disabled color="default">
          {i18n.t("connections.buttons.connecting")}
        </Button>
      );
    }
    /* status Pending */
    const loadInfo = isLoadingQueue.find((item) => item.id === whatsApp.id)
    const isLoad = loadInfo?.isLoading && loadInfo.type === 'Connection'
    return (
      <ButtonWithSpinner
        loading={isLoad}
        disabled={isLoad}
        style={{ /** cancelar estilos del componente */ }}
        size="small"
        variant="outlined"
        color="secondary"
        onClick={() => handleRequestNewQrCode(whatsApp.id)}
        className={classes.buttonQR}
      >
        {i18n.t("connections.buttons.newQr")}
      </ButtonWithSpinner>
    );
  };

  const renderCloudQualityBadge = (whatsApp) => {
    const rating = normalizeQualityRating(whatsApp.qualityRating);
    if (!rating) {
      return null;
    }

    const qualityKey = rating.toLowerCase();
    const indicatorType = getQualityIndicatorType(rating);
    const limitLine = whatsApp.messagingLimit
      ? i18n.t("connections.toolTips.cloudApi.quality.limit", {
          limit: whatsApp.messagingLimit,
        })
      : null;
    const metaLink = (
      <a
        href={META_BUSINESS_SUITE_PHONE_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "inherit", fontWeight: 600 }}
      >
        {i18n.t("connections.toolTips.cloudApi.quality.openMeta")}
      </a>
    );

    return (
      <CustomToolTip
        title={i18n.t(`connections.toolTips.cloudApi.quality.${qualityKey}.title`)}
        content={
          <>
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              {i18n.t(`connections.toolTips.cloudApi.quality.${qualityKey}.content`)}
            </Typography>
            {limitLine && (
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                {limitLine}
              </Typography>
            )}
            {(rating === "RED" || rating === "YELLOW") && metaLink}
          </>
        }
      >
        <StatusIndicator
          type={indicatorType}
          message={`Quality ${rating}`}
          customIcon={rating === "GREEN" ? CheckCircleOutlineIcon : ErrorOutlineIcon}
        />
      </CustomToolTip>
    );
  };

  const renderStatusToolTips = (whatsApp) => {
    if (isMessengerPageChannel(whatsApp.channel)) {
      switch (whatsApp.status) {
        case "CONNECTED":
          return (
            <CustomToolTip
              title="Messenger conectado"
              content="La Page está registrada y recibiendo mensajes vía webhook."
            >
              <StatusIndicator type="Success" message="Messenger conectado" customIcon={CheckCircleOutlineIcon} />
            </CustomToolTip>
          );
        case "DISCONNECTED":
          return (
            <CustomToolTip
              title="Messenger desconectado"
              content="Vuelve a autorizar la Page con Meta."
            >
              <StatusIndicator type="Danger" message="Messenger desconectado" customIcon={ErrorOutlineIcon} />
            </CustomToolTip>
          );
        default:
          return (
            <CustomToolTip
              title="Pendiente Meta"
              content="Esperando register-line o webhook."
            >
              <StatusIndicator type="Warning" message="Pendiente Meta" customIcon={CropFreeIcon} />
            </CustomToolTip>
          );
      }
    }

    if (isWhatsappApiChannel(whatsApp.channel)) {
      switch (whatsApp.status) {
        case "CONNECTED":
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <CustomToolTip
                title={i18n.t("connections.toolTips.cloudApi.connected.title")}
                content={i18n.t("connections.toolTips.cloudApi.connected.content")}
              >
                <StatusIndicator type="Success" message="Cloud conectado" customIcon={CheckCircleOutlineIcon} />
              </CustomToolTip>
              {renderCloudQualityBadge(whatsApp)}
            </div>
          );
        case "DISCONNECTED":
          return (
            <CustomToolTip
              title={i18n.t("connections.toolTips.cloudApi.invalid.title")}
              content={i18n.t("connections.toolTips.cloudApi.invalid.content")}
            >
              <StatusIndicator type="Danger" message="Cloud desconectado" customIcon={ErrorOutlineIcon} />
            </CustomToolTip>
          );
        default:
          return (
            <CustomToolTip
              title={i18n.t("connections.toolTips.cloudApi.pending.title")}
              content={i18n.t("connections.toolTips.cloudApi.pending.content")}
            >
              <StatusIndicator type="Warning" message="Pendiente Meta" customIcon={ErrorOutlineIcon} />
            </CustomToolTip>
          );
      }
    }

    // Para conexiones de WebChat, mostramos un estado diferente
    if (whatsApp.channel === "webchat") {
      switch (whatsApp.status) {
        case 'CONNECTED':
          return (
            <CustomToolTip
              title={i18n.t("connections.toolTips.connected.title")}
            >
              <StatusIndicator type="Success" message="Conectado" customIcon={CheckCircleOutlineIcon}/>
            </CustomToolTip>
          );
        case 'DISCONNECTED':
          return (
            <CustomToolTip
              title={i18n.t("connections.toolTips.disconnected.title")}
              content={i18n.t("connections.toolTips.disconnected.content")}
              style={{ marginTop: 5 }}
            >
              <StatusIndicator type="Danger" message="Desconectado" customIcon={ErrorOutlineIcon} />
            </CustomToolTip>
          );
        default :
          return ;
      }
    }
    switch (whatsApp.status) {
      case 'PENDING':
        return (
            <CustomToolTip
              title={i18n.t("connections.toolTips.disconnected.title")}
              content={i18n.t("connections.toolTips.disconnected.content")}
              style={{ marginTop: 5 }}
            >
              <StatusIndicator type="Danger" message="Desconectado" customIcon={ErrorOutlineIcon} />
            </CustomToolTip>
          );
      case 'OPENING':
        return (<CircularProgress size={24} />)
      case 'qrcode':
        return (<CustomToolTip
          title={i18n.t("connections.toolTips.qrcode.title")}
          content={i18n.t("connections.toolTips.qrcode.content")}
        >
          <StatusIndicator type="Info" message="Escanear QR" customIcon={CropFreeIcon} />
        </CustomToolTip>)
      case 'CONNECTED':
        return (<CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
          <StatusIndicator type="Success" message="Conectado" customIcon={CheckCircleOutlineIcon}/>
        </CustomToolTip>)
      default: // ["TIMEOUT", "PAIRING"]
        return (<CustomToolTip
          title={i18n.t("connections.toolTips.timeout.title")}
          content={i18n.t("connections.toolTips.timeout.content")}
        >
          <StatusIndicator type="Warning" message="Fuera de línea" customIcon={SignalCellularConnectedNoInternet2BarIcon} />
        </CustomToolTip>)
    }
  };

  return (
    <>
      <div className={classes.mainContainer}>
        <ConfirmationModal
          title={confirmModalInfo.title}
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={handleSubmitConfirmationModal}
          confirmButtonText={confirmModalInfo.confirmButtonText}
          isLoadingConfirmation={isLoadingQueue.find((queue) => queue.id === confirmModalInfo.whatsAppId)?.isLoading}
          onCloseAfterConfirming={false}
        >
          {confirmModalInfo.message}
        </ConfirmationModal>
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
        />
        <WhatsAppModal
          open={whatsAppModalOpen}
          onClose={handleCloseWhatsAppModal}
          whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
          whatsApps={whatsApps}
        />
        <SelectChannelModal
          open={selectChannelModalOpen}
          onClose={() => setSelectChannelModalOpen(false)}
          onSelect={handleChannelSelect}
        />
        <WebChatModal
          open={webChatModalOpen}
          onClose={handleCloseWebChatModal}
          webChatId={selectedWebChatId}
        />
        <div className={classes.tableContainer}>
          <HeaderManager
            title={i18n.t("connections.title")}
            primaryButtons={primaryButtons} 
          />
          <TableManager
            columns={columns}
            data={whatsApps}
            loading={loading}
            actionButtons={actionButtons}
          />
        </div>
      </div>
    </>
  );
};

export default Connections;
